import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import { drawTargetCanvas, checkHitCanvas, getZoneScore } from '../Shared/utils/TargetUtils';
import Countdown from '../Shared/components/Countdown/Countdown';

const TARGETS = [
    { r: 28, pts: 50, color: "#3498db", tone: 300 },
    { r: 20, pts: 100, color: "#f1c40f", tone: 600 },
    { r: 14, pts: 200, color: "#e74c3c", tone: 1000 }
];
const NUM_TARGETS = 4;

const MotionClicker = () => {
    const canvasRef = useRef(null);
    const { beep } = useAudio();

    // Game State Refs
    const stateRef = useRef({
        targets: [],
        running: false,
        timeLeft: 0,
        score: 0,
        hits: 0,
        misses: 0,
        flashes: []
    });

    // UI State
    const [mode, setMode] = useState('easy');
    const [duration, setDuration] = useState(30);
    const [inputLag, setInputLag] = useState(250);
    const [targetType, setTargetType] = useState('circle');
    const [uiStats, setUiStats] = useState({ time: 0, score: 0, hits: 0, misses: 0 });
    const [scoreboard, setScoreboard] = useState([]);
    const [finishedRank, setFinishedRank] = useState(null);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [showStartModal, setShowStartModal] = useState(true);
    const [calibratedVal, setCalibratedVal] = useState(null);

    // Scoreboard Helpers
    const scoreKey = useCallback(() => "motion_clicker_scores_" + mode, [mode]);

    const loadBoard = useCallback(() => {
        const list = JSON.parse(localStorage.getItem(scoreKey()) || "[]");
        setScoreboard(list);
    }, [scoreKey]);

    const saveScore = useCallback(() => {
        const { score, hits, misses } = stateRef.current;
        const list = JSON.parse(localStorage.getItem(scoreKey()) || "[]");
        list.push({ score, hits, misses, date: new Date().toISOString() });
        list.sort((a, b) => b.score - a.score);

        // Find Rank
        const rank = list.findIndex(r => r.score === score && r.hits === hits && r.misses === misses) + 1;
        setFinishedRank(rank);

        localStorage.setItem(scoreKey(), JSON.stringify(list.slice(0, 10)));
        loadBoard();
    }, [scoreKey, loadBoard]);

    const resetBoard = () => {
        localStorage.removeItem(scoreKey());
        loadBoard();
    };

    useEffect(() => {
        loadBoard();
        const cal = localStorage.getItem('calibratedLatency');
        if (cal) {
            const val = Number(cal);
            setCalibratedVal(val);
            setInputLag(val); // Auto-apply
        }
    }, [loadBoard]);

    // Game Logic Helpers
    const randomTarget = useCallback((width, height) => {
        const t = TARGETS[Math.floor(Math.random() * TARGETS.length)];
        let speed = 180 * (t.pts === 200 ? 1.25 : 1);
        if (mode === "hard" || mode === "xhard") speed *= 1.3;

        const radius = targetType === 'target' ? 40 : t.r;

        let vx = 0, vy = 0;
        if (mode === "easy") {
            vx = (Math.random() < .5 ? -1 : 1) * speed;
        } else if (!mode.startsWith("swing")) {
            const a = Math.random() * Math.PI * 2;
            vx = Math.cos(a) * speed;
            vy = Math.sin(a) * speed;
        }

        return {
            ...t,
            x: Math.random() * (width - 2 * radius) + radius,
            y: Math.random() * (height - 2 * radius) + radius,
            vx, vy,
            swingCenter: Math.random() * (width - 400) + 200,
            swingPhase: Math.random() * Math.PI * 2
        };
    }, [mode, targetType]);

    const actualStart = useCallback(() => {
        beep(1000, 0.2); // Start signal
        const state = stateRef.current;
        state.score = 0;
        state.hits = 0;
        state.misses = 0;
        state.timeLeft = duration;
        state.running = true;
        state.targets = [];

        const { width, height } = canvasRef.current;
        for (let i = 0; i < NUM_TARGETS; i++) {
            state.targets.push(randomTarget(width, height));
        }
    }, [duration, randomTarget, beep]);

    const stageStart = useCallback(() => {
        setFinishedRank(null);
        setShowStartModal(false);
        setIsCountingDown(true);
    }, []);

    const startGame = useCallback(() => {
        stageStart();
    }, [stageStart]);

    // Game Loop
    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const lag = inputLag / 1000;

        if (state.running) {
            state.timeLeft -= dt;
            if (state.timeLeft <= 0) {
                state.timeLeft = 0;
                state.running = false;
                saveScore();
            }
        }

        // Update Flashes
        state.flashes = state.flashes.filter(f => f.life > 0);
        state.flashes.forEach(f => {
            f.life -= dt;
            f.y -= dt * 60; // Float up
        });

        setUiStats({
            time: state.timeLeft,
            score: state.score,
            hits: state.hits,
            misses: state.misses
        });

        for (const t of state.targets) {
            if (state.running) {
                if (mode.startsWith("swing")) {
                    t.swingPhase += dt * 2;
                    t.vx = Math.cos(t.swingPhase) * 300;
                    t.x = t.swingCenter + Math.sin(t.swingPhase) * 200;
                } else {
                    t.x += t.vx * dt;
                    t.y += t.vy * dt;
                }
            }

            if (t.x - (targetType === 'target' ? 40 : t.r) < 0 || t.x + (targetType === 'target' ? 40 : t.r) > canvas.width) t.vx *= -1;
            if (!mode.startsWith("swing")) {
                if (t.y - (targetType === 'target' ? 40 : t.r) < 0 || t.y + (targetType === 'target' ? 40 : t.r) > canvas.height) t.vy *= -1;
            }

            if (targetType === 'target') {
                drawTargetCanvas(ctx, t.x, t.y, 0.35, 0);
            } else {
                ctx.fillStyle = t.color;
                ctx.beginPath();
                ctx.arc(t.x, t.y, t.r, 0, Math.PI * 2);
                ctx.fill();
            }

            const hideLead = (mode === "xhard" || mode === "swinghard");
            if (!hideLead) {
                let lx, ly;
                if (mode.startsWith("swing")) {
                    lx = t.x + t.vx * lag;
                    ly = t.y;
                } else {
                    lx = t.x + t.vx * lag;
                    ly = t.y + t.vy * lag;
                }
                ctx.strokeStyle = "#2ecc71";
                ctx.beginPath();
                ctx.moveTo(lx - 10, ly); ctx.lineTo(lx + 10, ly);
                ctx.moveTo(lx, ly - 10); ctx.lineTo(lx, ly + 10);
                ctx.stroke();
            }
        }

        // Draw Flashes
        ctx.save();
        for (const f of state.flashes) {
            ctx.fillStyle = f.color ? f.color.replace('1.0)', `${f.life})`) : `rgba(46, 204, 113, ${f.life})`;
            ctx.font = `bold ${20 + (1 - f.life) * 10}px Inter, sans-serif`;
            ctx.textAlign = "center";
            ctx.fillText(f.text, f.x, f.y);
        }
        ctx.restore();
    }, [mode, inputLag, randomTarget, saveScore, duration, targetType]);

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        if (!state.running) return;

        const canvas = canvasRef.current;
        const r = canvas.getBoundingClientRect();
        const mx = e.clientX - r.left;
        const my = e.clientY - r.top;

        let hit = false;
        for (const t of state.targets) {
            let hitPts = 0;
            if (targetType === 'target') {
                hitPts = checkHitCanvas(mx, my, t.x, t.y, 0.35, 0);
            } else {
                if (Math.hypot(mx - t.x, my - t.y) <= t.r) {
                    hitPts = t.pts;
                }
            }

            if (hitPts > 0) {
                hit = true;
                state.hits++;
                state.score += hitPts;

                // Add Flash
                state.flashes.push({
                    x: mx,
                    y: my,
                    text: `+${hitPts}`,
                    color: 'rgba(46, 204, 113, 1.0)',
                    life: 1.0
                });

                beep(t.tone, 0.08);
                Object.assign(t, randomTarget(canvas.width, canvas.height));
                break;
            }
        }
        if (!hit) {
            state.misses++;
        }
    };

    const sidebarContent = (
        <>
            <label>Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="easy">Horizontal Only</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="xhard">Extra Hard (No Compensation)</option>
                <option value="swing">Pendulums</option>
                <option value="swinghard">Pendulums Hard (No Compensation)</option>
            </select>

            <label>Target Type</label>
            <select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                <option value="circle">Circles</option>
                <option value="target">Targets</option>
            </select>

            <label>Duration</label>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                <option value="30">30s</option>
                <option value="60">60s</option>
                <option value="120">120s</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Input Lag: <span>{inputLag}</span>ms</span>
                {calibratedVal !== null ? (
                    <button
                        onClick={() => setInputLag(calibratedVal)}
                        style={{
                            background: 'none', border: '1px solid #f1c40f', color: '#f1c40f',
                            borderRadius: '4px', cursor: 'pointer', fontSize: '10px', padding: '2px 5px',
                            display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                        title={`Use calibrated value: ${calibratedVal}ms`}
                    >
                        <span>★</span> USE {calibratedVal}ms
                    </button>
                ) : (
                    <Link to="/test-latency" style={{ fontSize: '10px', color: '#aaa', textDecoration: 'underline' }}>
                        Calibrate?
                    </Link>
                )}
            </label>
            <input
                type="range"
                min="0"
                max="500"
                value={inputLag}
                onChange={(e) => setInputLag(Number(e.target.value))}
            />

            <button onClick={startGame}>Start Game</button>

            <Scoreboard
                stats={{
                    "Time": uiStats.time.toFixed(1),
                    "Score": uiStats.score,
                    "Hits": uiStats.hits,
                    "Misses": uiStats.misses
                }}
                highScores={scoreboard}
                onReset={resetBoard}
                title={`Scoreboard (${mode})`}
            />
        </>
    );

    const handleCountdownFinish = useCallback(() => {
        setIsCountingDown(false);
        actualStart();
    }, [actualStart]);

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={stateRef.current.running || isCountingDown}
            onQuit={() => {
                stateRef.current.running = false;
                stateRef.current.timeLeft = 0;
                setUiStats(prev => ({ ...prev, time: 0 }));
                setIsCountingDown(false);
                setShowStartModal(true);
            }}
        >
            {isCountingDown && (
                <Countdown onFinish={handleCountdownFinish} />
            )}
            {showStartModal && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '50px', borderRadius: '16px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', minWidth: '350px'
                    }}>
                        <h1 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2.5em', marginBottom: '10px' }}>Motion Clicker</h1>
                        <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em' }}>
                            Test your motion clicking skills.<br />
                            Click the targets based on movement and lag!
                        </p>
                        <button
                            onClick={startGame}
                            style={{
                                padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
                                transition: 'all 0.2s', boxShadow: '0 4px 0 #2980b9'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#2980b9'}
                            onMouseOut={(e) => e.target.style.background = '#3498db'}
                        >
                            START GAME
                        </button>
                    </div>
                </div>
            )}
            {finishedRank !== null && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '40px', borderRadius: '12px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', minWidth: '300px'
                    }}>
                        <h2 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2em' }}>Time&apos;s Up!</h2>

                        <div style={{ margin: '20px 0', fontSize: '1.2em' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <span style={{ fontSize: '2.5em', fontWeight: 'bold' }}>{uiStats.score}</span>
                                <span style={{ color: '#aaa', marginLeft: '10px' }}>PTS</span>
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                                Hits: {uiStats.hits} | Misses: {uiStats.misses}
                            </div>
                        </div>

                        <div style={{
                            background: '#333', padding: '10px', borderRadius: '5px', marginBottom: '25px', border: '1px solid #555'
                        }}>
                            You placed <strong style={{ color: '#2ecc71', fontSize: '1.2em' }}>#{finishedRank}</strong> for {mode} mode!
                        </div>

                        <button
                            onClick={startGame}
                            style={{
                                padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                                background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
                            }}
                        >
                            Play Again
                        </button>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

export default MotionClicker;
